import React, { useState } from 'react';
import {
  Alert,
  App,
  Button,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  TimePicker,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, EditOutlined, DeleteOutlined, TeamOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';

const ShiftsPage: React.FC = () => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [form] = Form.useForm();

  const { data: rows = [], isLoading } = useQuery({
    queryKey: hrmsKeys.shifts(),
    queryFn: () => hrmsService.listShifts(),
  });

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      break_minutes: 60,
      expected_hours: 8,
      grace_minutes: 15,
      is_active: true,
      is_night: false,
    });
    setDrawerOpen(true);
  };

  const openEdit = (r: any) => {
    setEditing(r);
    form.setFieldsValue({
      ...r,
      start_time: r.start_time ? dayjs(r.start_time, 'HH:mm:ss') : undefined,
      end_time: r.end_time ? dayjs(r.end_time, 'HH:mm:ss') : undefined,
    });
    setDrawerOpen(true);
  };

  const save = async () => {
    try {
      const v = await form.validateFields();
      const body = {
        ...v,
        start_time: v.start_time?.format('HH:mm:ss'),
        end_time: v.end_time?.format('HH:mm:ss'),
      };
      if (editing) await hrmsService.updateShift(editing.id, body);
      else await hrmsService.createShift(body);
      message.success(editing ? 'Updated' : 'Created');
      setDrawerOpen(false);
      queryClient.invalidateQueries({ queryKey: hrmsKeys.shifts() });
    } catch {
      message.error('Save failed');
    }
  };

  const remove = async (id: string) => {
    try {
      await hrmsService.deleteShift(id);
      message.success('Deleted');
      queryClient.invalidateQueries({ queryKey: hrmsKeys.shifts() });
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Delete failed');
    }
  };

  const columns: ColumnsType<any> = [
    { title: 'Code', dataIndex: 'shift_code', width: 100 },
    { title: 'Name', dataIndex: 'shift_name' },
    { title: 'Start', dataIndex: 'start_time', width: 100 },
    { title: 'End', dataIndex: 'end_time', width: 100 },
    { title: 'Hours', dataIndex: 'expected_hours', width: 80 },
    {
      title: 'Grace',
      dataIndex: 'grace_minutes',
      width: 90,
      render: (v: number) => `${v ?? 0} min`,
    },
    {
      title: 'Night',
      dataIndex: 'is_night',
      width: 80,
      render: (v: boolean) => (v ? <Tag color="purple">Night</Tag> : '—'),
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      width: 80,
      render: (v: boolean) => <Tag color={v ? 'green' : 'default'}>{v ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: 'Actions',
      key: 'a',
      fixed: 'right',
      width: 130,
      render: (_, r) => (
        <Space>
          <Button
            type="text"
            size="small"
            icon={<TeamOutlined />}
            title="Employees on this shift"
            onClick={() => navigate(`/hr/employees/shift-assignment?shift=${r.id}`)}
          />
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title="Delete shift?" onConfirm={() => remove(r.id)}>
            <Button type="text" danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Shifts"
          subtitle="Working time templates for attendance, late calculation, and OT"
          primaryActions={
            <Space wrap>
              <Button icon={<TeamOutlined />} onClick={() => navigate('/hr/employees/shift-assignment')}>
                Assign employees
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                Add shift
              </Button>
            </Space>
          }
        />
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Shift vs company policy"
        description={
          <span>
            Company-wide late grace on <Link to="/hr/setup">HRMS Setup</Link> is the fallback. Each shift can set its
            own grace minutes and expected hours. Give employees a shift with{' '}
            <Link to="/hr/employees/shift-assignment">Shift assignment</Link> (bulk, permanent or for a period) so
            attendance and auto-generated timesheets know when work starts. A shift in use cannot be deleted — mark it
            inactive instead.
          </span>
        }
      />

      <Table
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 20 }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No shifts yet — add General (08:00–17:00) and Night if you run 24×7"
            />
          ),
        }}
      />

      <Drawer
        title={editing ? 'Edit shift' : 'New shift'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={440}
        destroyOnClose
        extra={
          <Button type="primary" onClick={save}>
            Save
          </Button>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="shift_code"
            label="Code"
            rules={[{ required: true }]}
            help="Short unique code, e.g. GEN, NIGHT"
          >
            <Input disabled={Boolean(editing)} placeholder="GEN" />
          </Form.Item>
          <Form.Item name="shift_name" label="Display name" rules={[{ required: true }]}>
            <Input placeholder="General day shift" />
          </Form.Item>
          <Form.Item
            name="start_time"
            label="Start time"
            rules={[{ required: true }]}
            help="Clock-in expectation for Present / late calculation"
          >
            <TimePicker style={{ width: '100%' }} format="HH:mm" />
          </Form.Item>
          <Form.Item name="end_time" label="End time" rules={[{ required: true }]}>
            <TimePicker style={{ width: '100%' }} format="HH:mm" />
          </Form.Item>
          <Form.Item
            name="break_minutes"
            label="Break"
            help="Unpaid or paid break length deducted from expected hours when computing attendance"
          >
            <InputNumber style={{ width: '100%' }} min={0} addonAfter="minutes" />
          </Form.Item>
          <Form.Item name="expected_hours" label="Expected working hours" help="Standard paid hours for a full day">
            <InputNumber style={{ width: '100%' }} min={0} step={0.5} addonAfter="hours" />
          </Form.Item>
          <Form.Item
            name="grace_minutes"
            label="Late grace"
            help="Minutes after start before the employee is marked late (overrides company default when set)"
          >
            <InputNumber style={{ width: '100%' }} min={0} addonAfter="minutes" />
          </Form.Item>
          <Form.Item
            name="is_night"
            label="Night shift"
            valuePropName="checked"
            help="Flags overnight shifts for OT / differential reporting"
          >
            <Switch />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Drawer>
    </WorkspaceLayout>
  );
};

export default ShiftsPage;
